import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { HardDriveDownload, Smartphone, Share, PlusSquare, X, Download } from "lucide-react";
import { useStore } from "../context/StoreContext";
import { dateLabel } from "../lib/format";
import { onPrimary } from "./MoveHelpers";
import { Button } from "./ui/button";

const LAST = "hb-last-backup";
const FIRST = "hb-first-seen";
const SNOOZE = "hb-backup-snooze";
const DAY = 86400000;

export const recordBackup = () => localStorage.setItem(LAST, new Date().toISOString());
export const lastBackup = () => localStorage.getItem(LAST);

export function backupDue(now = Date.now()) {
  if (!localStorage.getItem(FIRST)) localStorage.setItem(FIRST, new Date(now).toISOString());
  if (Number(localStorage.getItem(SNOOZE) || 0) > now) return false;
  const ref = lastBackup() || localStorage.getItem(FIRST);
  return now - new Date(ref).getTime() >= 30 * DAY;
}

export function BackupReminder() {
  const { state } = useStore();
  const [due, setDue] = useState(() => backupDue());
  if (!due || !state.transactions.length) return null;
  const last = lastBackup();
  const snooze = () => { localStorage.setItem(SNOOZE, String(Date.now() + 7 * DAY)); setDue(false); };
  return (
    <div className="rounded-2xl border bg-card px-4 py-3 flex flex-wrap items-center gap-3 text-sm" data-testid="backup-reminder">
      <HardDriveDownload className="h-4 w-4 text-primary shrink-0" />
      <span className="flex-1 min-w-[220px]"><b>Time for a backup.</b> <span className="text-muted-foreground" data-testid="backup-reminder-last">{last ? `Last backup: ${dateLabel(last.slice(0, 10))}.` : "You haven't made a backup yet."} Your data lives only on this device — a backup keeps it safe if the device is lost.</span></span>
      <Button size="sm" asChild><Link to="/settings?backup=1" data-testid="backup-reminder-now">Back up now</Link></Button>
      <Button size="sm" variant="ghost" onClick={snooze} data-testid="backup-reminder-snooze">Remind me in a week</Button>
    </div>
  );
}

const VISITS = "hb-visits";
const INSTALL_DONE = "hb-install-done";
const standalone = () => window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;

function platform() {
  const ua = navigator.userAgent;
  const iOS = /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
  if (iOS) return "ios";
  if (/Macintosh/.test(ua) && /Safari/.test(ua) && !/Chrome|Chromium|Edg|Firefox/.test(ua)) return "mac-safari";
  return "chrome";
}

const STEPS = {
  ios: [[Share, "Tap the Share button in Safari"], [PlusSquare, "Choose “Add to Home Screen”"], [Smartphone, "Tap Add — open it from your Home Screen like any app"]],
  "mac-safari": [[Share, "In Safari, open the File menu (or Share button)"], [PlusSquare, "Choose “Add to Dock…”"], [Smartphone, "Click Add — it opens in its own window"]],
  chrome: [[Download, "Click the Install icon in the address bar, or use the button here"], [PlusSquare, "Confirm “Install”"], [Smartphone, "Open it from your apps or dock"]],
};

let deferred = null;
if (typeof window !== "undefined") window.addEventListener("beforeinstallprompt", (e) => { e.preventDefault(); deferred = e; });

// Counts one visit per browser session
if (typeof window !== "undefined" && !sessionStorage.getItem("hb-visit-counted")) {
  sessionStorage.setItem("hb-visit-counted", "1");
  localStorage.setItem(VISITS, String(Number(localStorage.getItem(VISITS) || 0) + 1));
}

export function InstallGuide() {
  const [hidden, setHidden] = useState(() => !onPrimary() || standalone() || localStorage.getItem(INSTALL_DONE) === "1" || Number(localStorage.getItem(VISITS) || 0) < 2);
  const [canPrompt, setCanPrompt] = useState(!!deferred);
  useEffect(() => {
    const on = () => setCanPrompt(true);
    const done = () => { localStorage.setItem(INSTALL_DONE, "1"); setHidden(true); };
    window.addEventListener("beforeinstallprompt", on);
    window.addEventListener("appinstalled", done);
    return () => { window.removeEventListener("beforeinstallprompt", on); window.removeEventListener("appinstalled", done); };
  }, []);
  if (hidden) return null;
  const p = platform();
  const close = () => { localStorage.setItem(INSTALL_DONE, "1"); setHidden(true); };
  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const r = await deferred.userChoice;
    deferred = null;
    setCanPrompt(false);
    if (r.outcome === "accepted") close();
  };
  return (
    <div className="rounded-2xl border-2 border-primary/30 bg-accent/50 p-4 text-sm relative" data-testid="install-guide" data-platform={p}>
      <button className="absolute right-3 top-3 text-muted-foreground hover:text-foreground" onClick={close} aria-label="Dismiss" data-testid="install-guide-dismiss"><X className="h-4 w-4" /></button>
      <p className="font-semibold flex items-center gap-2"><Smartphone className="h-4 w-4 text-primary" /> Open your budget like a real app</p>
      <p className="text-muted-foreground mt-1 pr-6">Add it to your {p === "ios" ? "Home Screen" : p === "mac-safari" ? "Dock" : "apps"} for one-tap access, full screen and offline use. Your data stays exactly where it is.</p>
      <ol className="grid sm:grid-cols-3 gap-2 mt-3">
        {STEPS[p].map(([Icon, text], i) => (
          <li key={i} className="rounded-xl bg-background/80 border p-3 flex gap-2 items-start" data-testid={`install-step-${i}`}>
            <span className="num text-primary font-semibold">{i + 1}</span><Icon className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" /><span>{text}</span>
          </li>
        ))}
      </ol>
      {p === "chrome" && canPrompt && <Button size="sm" className="mt-3" onClick={install} data-testid="install-guide-button"><Download className="h-4 w-4 mr-1" /> Install app</Button>}
    </div>
  );
}
